import { useGetBriefDeliverableConfig } from "@entities/brief/lib/hooks/useGetBriefDeliverableConfig";
import { useGetBriefCampaigns } from "@entities/campaigns/lib/hooks";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import {
  BriefCampaignInf,
  IBriefCampaignInfluencerSearchRequestV2,
} from "@shared/lib/types/briefCampains";
import { IBriefDeliverableConfig } from "@shared/lib/types/briefConfig";
import {
  ExecutionStatusFilter,
  ExecutionStatusFilterEnum,
} from "@widgets/CampaignExecution/constants";
import {
  CampaignPendingReviewFlag,
  getCampaignPendingReviewSearchParams,
} from "@widgets/CampaignExecution/lib";
import { GanttChart } from "@widgets/GanttChart";
import { Spin } from "antd";
import React, { ReactNode, useEffect, useMemo } from "react";
import styles from "./CampaignExecutionGantt.module.scss";
import { adaptCampaignInfluencersToGantt } from "./ganttAdapter";
import { useCampaignGanttTimeline } from "./hooks/useCampaignGanttTimeline";

export interface CampaignExecutionGanttProps {
  title?: ReactNode;
  config?: IBriefDeliverableConfig[];
  campaignInfluencers?: BriefCampaignInf[];
  filters?: IBriefCampaignInfluencerSearchRequestV2;
  searchQuery?: string;
  activePendingReviewFlag?: string | null;
  isVendorBillingAttached?: boolean;
  financeFlags?: IBriefCampaignInfluencerSearchRequestV2["financeFlags"];
  assignedOperationsPoc?: string[];
  activeStatusFilter?: ExecutionStatusFilter;
  selectedDeliverableId?: string | null;
  onOpenDeliverable?: (
    creatorId: string,
    deliverableId?: string,
    taskId?: string
  ) => void;
  campaignStartDate?: string;
  campaignEndDate?: string;
  scrollY?: number | string;
}

export const CampaignExecutionGantt: React.FC<CampaignExecutionGanttProps> = ({
  title,
  config,
  campaignInfluencers,
  filters,
  searchQuery,
  activePendingReviewFlag,
  isVendorBillingAttached,
  financeFlags,
  assignedOperationsPoc,
  activeStatusFilter,
  selectedDeliverableId,
  onOpenDeliverable,
  campaignStartDate,
  campaignEndDate,
  scrollY,
}) => {
  const { briefId } = useBriefStore();

  const { config: fetchedConfig } = useGetBriefDeliverableConfig(
    config ? "" : (briefId || "")
  );
  const activeConfig = config || fetchedConfig;

  const campaignSearchPayload = useMemo(
    () => ({
      briefId: briefId || "",
      searchQuery: searchQuery || undefined,
      status: activeStatusFilter || ExecutionStatusFilterEnum.All,
      // Note: limit is omitted from payload so all creators are fetched for the Gantt chart
      isVendorBillingAttached,
      financeFlags,
      assignedOperationsPoc,
      deliverables: filters?.deliverables,
      dynamicFields: filters?.dynamicFields,
      sortByDynamicFields: filters?.sortByDynamicFields,
      ...getCampaignPendingReviewSearchParams(
        (activePendingReviewFlag as CampaignPendingReviewFlag) || null
      ),
    }),
    [
      briefId,
      searchQuery,
      activeStatusFilter,
      isVendorBillingAttached,
      financeFlags,
      assignedOperationsPoc,
      filters?.deliverables,
      filters?.dynamicFields,
      filters?.sortByDynamicFields,
      activePendingReviewFlag,
    ]
  );

  const isApiEnabled = Boolean(briefId) && !campaignInfluencers;

  const {
    flatData: apiInfluencers,
    isLoading: isApiLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useGetBriefCampaigns(campaignSearchPayload, isApiEnabled);

  // In case backend paginates even when limit is not provided, fetch next pages automatically
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const activeInfluencers = useMemo(() => {
    if (campaignInfluencers) return campaignInfluencers;
    return apiInfluencers;
  }, [campaignInfluencers, apiInfluencers]);

  const allCreators = useMemo(() => {
    return adaptCampaignInfluencersToGantt(activeInfluencers, activeConfig);
  }, [activeInfluencers, activeConfig]);

  const creators = useMemo(() => {
    if (!selectedDeliverableId) return allCreators;
    return allCreators
      .map((c) => ({
        ...c,
        deliverables: c.deliverables.filter(
          (d) =>
            d.id === selectedDeliverableId ||
            d.name.toLowerCase() === selectedDeliverableId.toLowerCase()
        ),
      }))
      .filter((c) => c.deliverables.length > 0);
  }, [allCreators, selectedDeliverableId]);

  const {
    timelineByDeliverable,
    handleUpdateDeliverableTimeline,
    handleChangeDeliverableTimeline,
    handleApplyTimelineToAll,
    handleEqualizeAllTimelines,
    handleApplyStageDurations,
    handleDone,
    handleCancel,
    isSaving,
  } = useCampaignGanttTimeline({
    briefId,
    allCreators,
    activeInfluencers,
    selectedDeliverableId,
  });

  if (isApiLoading && allCreators.length === 0) {
    return (
      <div className={styles.loadingWrapper}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={styles.ganttWrapper}>
      <GanttChart
        title={title}
        creators={creators}
        timelineByDeliverable={timelineByDeliverable}
        campaignStartDate={campaignStartDate}
        campaignEndDate={campaignEndDate}
        onOpenDeliverable={onOpenDeliverable}
        onUpdateDeliverableTimeline={handleUpdateDeliverableTimeline}
        onChangeDeliverableTimeline={handleChangeDeliverableTimeline}
        onApplyTimelineToAll={handleApplyTimelineToAll}
        onEqualizeTimelines={handleEqualizeAllTimelines}
        onApplyStageDurations={handleApplyStageDurations}
        onDone={handleDone}
        onCancel={handleCancel}
        isSaving={isSaving}
        scrollY={scrollY}
      />
    </div>
  );
};
